/**
 * SoundService - DRAMATIC Synthesized Audio using Web Audio API
 * 
 * Creates impactful game sounds without external files:
 * - correct: Triumphant ascending fanfare with bass punch
 * - wrong: Noticeable buzzer with weight
 * - tick: Crisp click
 * - powerup: Epic rising sweep with sparkle explosion
 * - levelup: Grand ascending fanfare
 * - complete: Victory celebration!
 */

class SoundService {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.musicEnabled = false;
    this.musicOscillator = null;
    this.musicGain = null;

    console.log('[SoundService] ✅ DRAMATIC synthesized audio initialized');
  }

  // Lazy initialization of AudioContext (must be after user interaction)
  getAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browser policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    return this.audioContext;
  }

  // ============================================
  // DRAMATIC SOUND GENERATORS
  // ============================================

  /**
   * CORRECT - Triumphant ascending fanfare! 🎉
   * Big, bold, celebratory
   */
  playCorrect() {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // BASS PUNCH - Low frequency impact
    this.playTone({
      frequency: 130.81, // C3
      type: 'sine',
      startTime: now,
      duration: 0.2,
      volume: 0.6,
      attack: 0.01,
      decay: 0.19
    });

    // First hit - C5 with power
    this.playTone({
      frequency: 523.25,
      type: 'sine',
      startTime: now,
      duration: 0.2,
      volume: 0.7,
      attack: 0.01,
      decay: 0.19
    });

    // Add octave for richness
    this.playTone({
      frequency: 1046.5, // C6
      type: 'sine',
      startTime: now,
      duration: 0.15,
      volume: 0.4,
      attack: 0.01,
      decay: 0.14
    });

    // Second hit - E5 (major third up) - THE VICTORY NOTE
    this.playTone({
      frequency: 659.25,
      type: 'sine',
      startTime: now + 0.12,
      duration: 0.35,
      volume: 0.8,
      attack: 0.01,
      decay: 0.34
    });

    // E5 with fifth harmony
    this.playTone({
      frequency: 987.77, // B5
      type: 'sine',
      startTime: now + 0.12,
      duration: 0.3,
      volume: 0.4,
      attack: 0.01,
      decay: 0.29
    });

    // Sparkle burst
    [1318.5, 1568.0, 2093.0].forEach((freq, i) => {
      this.playTone({
        frequency: freq,
        type: 'sine',
        startTime: now + 0.15 + i * 0.03,
        duration: 0.25,
        volume: 0.35,
        attack: 0.01,
        decay: 0.24
      });
    });

    // Third hit - G5 for full major chord resolution
    this.playTone({
      frequency: 783.99,
      type: 'sine',
      startTime: now + 0.25,
      duration: 0.4,
      volume: 0.6,
      attack: 0.02,
      decay: 0.38
    });
  }

  /**
   * WRONG - Noticeable but not harsh buzzer 📛
   * Clear feedback something went wrong
   */
  playWrong() {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // LOW BASS THUD
    this.playTone({
      frequency: 82.41, // E2
      type: 'sine',
      startTime: now,
      duration: 0.3,
      volume: 0.7,
      attack: 0.01,
      decay: 0.29
    });

    // Buzzer tone 1
    this.playTone({
      frequency: 220, // A3
      type: 'sawtooth',
      startTime: now,
      duration: 0.25,
      volume: 0.4,
      attack: 0.01,
      decay: 0.24
    });

    // Buzzer tone 2 - dissonant
    this.playTone({
      frequency: 233.08, // Bb3 (half step up - dissonance!)
      type: 'sawtooth',
      startTime: now,
      duration: 0.25,
      volume: 0.35,
      attack: 0.01,
      decay: 0.24
    });

    // Descending slide
    this.playDescendingTone({
      startFreq: 300,
      endFreq: 150,
      type: 'triangle',
      startTime: now + 0.1,
      duration: 0.3,
      volume: 0.5
    });

    // Second thud
    this.playTone({
      frequency: 73.42, // D2
      type: 'sine',
      startTime: now + 0.2,
      duration: 0.35,
      volume: 0.6,
      attack: 0.02,
      decay: 0.33
    });
  }

  /**
   * TICK - Crisp, audible click ⏱️
   */
  playTick() {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Sharp click
    this.playTone({
      frequency: 1200,
      type: 'square',
      startTime: now,
      duration: 0.03,
      volume: 0.35,
      attack: 0.001,
      decay: 0.029
    });

    // Body
    this.playTone({
      frequency: 600,
      type: 'triangle',
      startTime: now,
      duration: 0.05,
      volume: 0.25,
      attack: 0.001,
      decay: 0.049
    });

    // Click
    this.playTone({
      frequency: 2400,
      type: 'sine',
      startTime: now,
      duration: 0.02,
      volume: 0.2,
      attack: 0.001,
      decay: 0.019
    });
  }

  /**
   * POWERUP - Epic rising sweep with explosion! ⚡
   */
  playPowerup() {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Rising sweep
    this.playRisingTone({
      startFreq: 200,
      endFreq: 800,
      type: 'sawtooth',
      startTime: now,
      duration: 0.3,
      volume: 0.4
    });

    // BASS FOUNDATION
    this.playTone({
      frequency: 65.41, // C2
      type: 'sine',
      startTime: now,
      duration: 0.4,
      volume: 0.5,
      attack: 0.01,
      decay: 0.39
    });

    // Quick arpeggio C-E-G-C
    const notes = [261.63, 329.63, 392.00, 523.25];
    notes.forEach((freq, i) => {
      this.playTone({
        frequency: freq,
        type: 'sine',
        startTime: now + i * 0.07,
        duration: 0.2,
        volume: 0.45,
        attack: 0.01,
        decay: 0.19
      });

      // Octave double
      this.playTone({
        frequency: freq * 2,
        type: 'sine',
        startTime: now + i * 0.07,
        duration: 0.15,
        volume: 0.25,
        attack: 0.01,
        decay: 0.14
      });
    });

    // EXPLOSION at the top! 💥
    setTimeout(() => {
      const ctx2 = this.getAudioContext();
      const now2 = ctx2.currentTime;

      // Burst of high frequencies
      [1046.5, 1318.5, 1568.0, 2093.0].forEach((freq, i) => {
        this.playTone({
          frequency: freq,
          type: 'sine',
          startTime: now2 + i * 0.02,
          duration: 0.35,
          volume: 0.4,
          attack: 0.01,
          decay: 0.34
        });
      });

      // Impact bass
      this.playTone({
        frequency: 130.81,
        type: 'sine',
        startTime: now2,
        duration: 0.3,
        volume: 0.6,
        attack: 0.01,
        decay: 0.29
      });
    }, 280);
  }

  /**
   * LEVELUP - Grand triumphant fanfare! 🏆
   */
  playLevelup() {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // OPENING BASS
    this.playTone({
      frequency: 65.41, // C2
      type: 'sine',
      startTime: now,
      duration: 0.6,
      volume: 0.7,
      attack: 0.02,
      decay: 0.58
    });

    // Triumphant fanfare: G4-C5-E5-G5
    const notes = [392.00, 523.25, 659.25, 783.99];

    notes.forEach((freq, i) => {
      // Main note - LOUD
      this.playTone({
        frequency: freq,
        type: 'sine',
        startTime: now + i * 0.12,
        duration: 0.35,
        volume: 0.65,
        attack: 0.02,
        decay: 0.33
      });

      // Fifth harmony
      this.playTone({
        frequency: freq * 1.5,
        type: 'sine',
        startTime: now + i * 0.12,
        duration: 0.3,
        volume: 0.35,
        attack: 0.02,
        decay: 0.28
      });

      // Octave up
      this.playTone({
        frequency: freq * 2,
        type: 'sine',
        startTime: now + i * 0.12 + 0.02,
        duration: 0.25,
        volume: 0.25,
        attack: 0.01,
        decay: 0.24
      });
    });

    // GRAND FINALE CHORD
    setTimeout(() => {
      const ctx2 = this.getAudioContext();
      const now2 = ctx2.currentTime;

      // Full C major chord across octaves
      [261.63, 523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((freq, i) => {
        this.playTone({
          frequency: freq,
          type: 'sine',
          startTime: now2 + i * 0.01,
          duration: 0.8,
          volume: 0.45 - (i * 0.03),
          attack: 0.05,
          decay: 0.75
        });
      });

      // Bass foundation
      this.playTone({
        frequency: 130.81,
        type: 'sine',
        startTime: now2,
        duration: 0.8,
        volume: 0.6,
        attack: 0.05,
        decay: 0.75
      });

      // Sparkle cascade
      [1568.0, 1975.5, 2093.0, 2637.0].forEach((freq, i) => {
        this.playTone({
          frequency: freq,
          type: 'sine',
          startTime: now2 + 0.15 + i * 0.05,
          duration: 0.4,
          volume: 0.3,
          attack: 0.01,
          decay: 0.39
        });
      });
    }, 500);
  }

  /**
   * COMPLETE - Victory celebration! 🎊
   */
  playComplete() {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // OPENING FANFARE
    // Bass drum hit
    this.playTone({
      frequency: 60,
      type: 'sine',
      startTime: now,
      duration: 0.3,
      volume: 0.8,
      attack: 0.01,
      decay: 0.29
    });

    // First phrase: C-E-G
    const phrase1 = [523.25, 659.25, 783.99];
    phrase1.forEach((freq, i) => {
      this.playTone({
        frequency: freq,
        type: 'sine',
        startTime: now + i * 0.12,
        duration: 0.25,
        volume: 0.6,
        attack: 0.02,
        decay: 0.23
      });

      // Octave up
      this.playTone({
        frequency: freq * 2,
        type: 'sine',
        startTime: now + i * 0.12,
        duration: 0.2,
        volume: 0.3,
        attack: 0.02,
        decay: 0.18
      });
    });

    // SECOND PHRASE - Higher, triumphant!
    setTimeout(() => {
      const ctx2 = this.getAudioContext();
      const now2 = ctx2.currentTime;

      // Bass hit
      this.playTone({
        frequency: 130.81,
        type: 'sine',
        startTime: now2,
        duration: 0.8,
        volume: 0.7,
        attack: 0.02,
        decay: 0.78
      });

      // Full triumphant chord
      [1046.5, 1318.5, 1568.0].forEach((freq, i) => {
        this.playTone({
          frequency: freq,
          type: 'sine',
          startTime: now2 + i * 0.02,
          duration: 0.7,
          volume: 0.55,
          attack: 0.03,
          decay: 0.67
        });
      });

      // Sparkle cascade!
      [1568.0, 1975.5, 2093.0, 2349.3, 2637.0].forEach((freq, i) => {
        this.playTone({
          frequency: freq,
          type: 'sine',
          startTime: now2 + 0.1 + i * 0.06,
          duration: 0.4,
          volume: 0.35,
          attack: 0.01,
          decay: 0.39
        });
      });
    }, 400);

    // FINALE - Big chord with decay
    setTimeout(() => {
      const ctx3 = this.getAudioContext();
      const now3 = ctx3.currentTime;

      // Epic bass
      this.playTone({
        frequency: 65.41,
        type: 'sine',
        startTime: now3,
        duration: 1.2,
        volume: 0.8,
        attack: 0.05,
        decay: 1.15
      });

      // Full orchestra chord
      [261.63, 392.0, 523.25, 659.25, 783.99, 1046.5, 1318.5, 1568.0].forEach((freq, i) => {
        this.playTone({
          frequency: freq,
          type: 'sine',
          startTime: now3,
          duration: 1.0,
          volume: 0.4 - (i * 0.03),
          attack: 0.05,
          decay: 0.95
        });
      });

      // Final sparkle shower
      for (let i = 0; i < 8; i++) {
        this.playTone({
          frequency: 2000 + Math.random() * 1500,
          type: 'sine',
          startTime: now3 + 0.2 + i * 0.08,
          duration: 0.3,
          volume: 0.2,
          attack: 0.01,
          decay: 0.29
        });
      }
    }, 800);
  }

  // ============================================
  // CORE AUDIO ENGINE
  // ============================================

  /**
   * Play a single tone with envelope
   */
  playTone({ frequency, type = 'sine', startTime, duration, volume = 0.5, attack = 0.01, decay = 0.1 }) {
    const ctx = this.getAudioContext();

    // Create oscillator
    const oscillator = ctx.createOscillator();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startTime);

    // Create gain for envelope
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + attack);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + attack + decay);

    // Connect
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Play
    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.1);
  }

  /**
   * Play a rising tone (sweep up)
   */
  playRisingTone({ startFreq, endFreq, type = 'sine', startTime, duration, volume = 0.5 }) {
    const ctx = this.getAudioContext();

    const oscillator = ctx.createOscillator();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(startFreq, startTime);
    oscillator.frequency.exponentialRampToValueAtTime(endFreq, startTime + duration);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.1);
  }

  /**
   * Play a descending tone (sweep down)
   */
  playDescendingTone({ startFreq, endFreq, type = 'sine', startTime, duration, volume = 0.5 }) {
    const ctx = this.getAudioContext();

    const oscillator = ctx.createOscillator();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(startFreq, startTime);
    oscillator.frequency.exponentialRampToValueAtTime(endFreq, startTime + duration);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(volume, startTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.1);
  }

  // ============================================
  // PUBLIC API (matches old interface)
  // ============================================

  play(soundName) {
    if (!this.enabled) return;

    try {
      switch (soundName) {
        case 'correct':
          this.playCorrect();
          break;
        case 'wrong':
          this.playWrong();
          break;
        case 'tick':
          this.playTick();
          break;
        case 'powerup':
          this.playPowerup();
          break;
        case 'levelup':
          this.playLevelup();
          break;
        case 'complete':
          this.playComplete();
          break;
        default:
          console.warn(`[SoundService] Unknown sound: ${soundName}`);
      }
    } catch (error) {
      console.warn('[SoundService] Error playing sound:', error);
    }
  }

  stop(soundName) {
    // Synthesized sounds auto-stop, no action needed
  }

  // Background music - ambient drone
  startBackgroundMusic() {
    if (this.musicOscillator) return;

    try {
      const ctx = this.getAudioContext();
      this.musicEnabled = true;

      this.musicGain = ctx.createGain();
      this.musicGain.gain.setValueAtTime(0, ctx.currentTime);
      this.musicGain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 2);
      this.musicGain.connect(ctx.destination);

      // Low drone
      this.musicOscillator = ctx.createOscillator();
      this.musicOscillator.type = 'sine';
      this.musicOscillator.frequency.setValueAtTime(65.41, ctx.currentTime);
      this.musicOscillator.connect(this.musicGain);
      this.musicOscillator.start();

      // Fifth
      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(98.00, ctx.currentTime);
      const gain2 = ctx.createGain();
      gain2.gain.setValueAtTime(0.08, ctx.currentTime);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start();
      this.musicOscillator2 = osc2;
      this.musicGain2 = gain2;

      console.log('[SoundService] ✅ Background music started');
    } catch (error) {
      console.warn('[SoundService] Could not start background music:', error);
    }
  }

  stopBackgroundMusic() {
    this.musicEnabled = false;

    try {
      if (this.musicOscillator) {
        this.musicGain.gain.exponentialRampToValueAtTime(0.001, this.getAudioContext().currentTime + 0.5);
        this.musicOscillator.stop(this.getAudioContext().currentTime + 0.6);
        this.musicOscillator = null;
      }
      if (this.musicOscillator2) {
        this.musicGain2.gain.exponentialRampToValueAtTime(0.001, this.getAudioContext().currentTime + 0.5);
        this.musicOscillator2.stop(this.getAudioContext().currentTime + 0.6);
        this.musicOscillator2 = null;
      }
      console.log('[SoundService] ✅ Background music stopped');
    } catch (error) {
      // Ignore
    }
  }

  toggleMusic() {
    this.musicEnabled = !this.musicEnabled;
    if (this.musicEnabled) {
      this.startBackgroundMusic();
    } else {
      this.stopBackgroundMusic();
    }
    return this.musicEnabled;
  }

  toggleSFX() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  isMusicEnabled() {
    return this.musicEnabled;
  }

  isSFXEnabled() {
    return this.enabled;
  }
}

export const soundService = new SoundService();
